import { MutationCache, QueryClient, type MutationOptions, type MutationState } from '@tanstack/react-query';

let privacyGeneration = 0;

class PrivateMutationCache extends MutationCache {
  override build<TData, TError, TVariables, TContext>(
    client: QueryClient,
    options: MutationOptions<TData, TError, TVariables, TContext>,
    state?: MutationState<TData, TError, TVariables, TContext>,
  ) {
    const generation = privacyGeneration;
    const current = () => generation === privacyGeneration;
    const assertCurrent = () => {
      if (!current()) { const error = new Error('Authentication identity changed'); error.name = 'AbortError'; throw error; }
    };
    const guard = (next: typeof options): typeof options => {
      const mutationFn = next.mutationFn;
      const onMutate = next.onMutate;
      return {
        ...next,
        mutationFn: mutationFn ? async (...args) => { assertCurrent(); const data = await mutationFn(...args); assertCurrent(); return data; } : undefined,
        onMutate: onMutate ? async (...args) => { assertCurrent(); const context = await onMutate(...args); assertCurrent(); return context; } : undefined,
        onSuccess: (...args) => current() ? next.onSuccess?.(...args) : undefined,
        onError: (...args) => current() ? next.onError?.(...args) : undefined,
        onSettled: (...args) => current() ? next.onSettled?.(...args) : undefined,
      };
    };
    const mutation = super.build(client, guard(client.defaultMutationOptions(options)), state);
    const setOptions = mutation.setOptions.bind(mutation);
    mutation.setOptions = (next) => setOptions(guard(next));
    return mutation;
  }
}

export const queryClient = new QueryClient({
  mutationCache: new PrivateMutationCache(),
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

export const publicQueryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false } },
});

export function clearPrivateCache(): void {
  privacyGeneration++;
  void queryClient.cancelQueries();
  queryClient.clear();
}
